import type { components } from "@/generated/openapi-types";
import { cn } from "@/lib/cn";

type Photo = components["schemas"]["AdminPhoto"];

const REASON: Record<string, string> = {
  pending: "queued",
  processing: "processing",
  failed: "failed",
};

/**
 * One photo, or an honest account of why there is no photo.
 *
 * The API returns `url: null` whenever the bytes are not reachable — still
 * processing, failed, or a local run with no photo origin configured — and
 * this is where that becomes something to look at. The hatch matters: a
 * plain empty box reads as a broken image, and on the failed-photos queue
 * the whole point is telling "we could not process this" apart from "the
 * console is broken".
 */
export function PhotoTile({
  photo,
  size = 84,
}: {
  photo: Photo;
  size?: number;
}) {
  const label = REASON[photo.status] ?? "no origin";
  return (
    <figure
      className={cn(
        "relative overflow-hidden rounded-control border border-border bg-surface-2",
        photo.status === "failed" && "border-danger/30",
      )}
      style={{ width: size, height: size }}
      title={`${photo.kind} · ${photo.status}`}
    >
      {photo.url ? (
        <img
          src={photo.url}
          alt={photo.kind}
          loading="lazy"
          className="h-full w-full object-cover"
        />
      ) : (
        <>
          <span
            aria-hidden="true"
            className="absolute inset-0 opacity-60"
            style={{
              backgroundImage:
                "repeating-linear-gradient(45deg, rgb(var(--color-border)) 0 1px, transparent 1px 7px)",
            }}
          />
          <figcaption
            className={cn(
              "absolute inset-x-0 bottom-0 bg-surface/85 px-1 py-0.5 text-center text-[9.5px] leading-tight",
              photo.status === "failed" ? "text-danger" : "text-muted",
            )}
          >
            {label}
          </figcaption>
        </>
      )}
    </figure>
  );
}
